'use client';

import React, { useEffect, useState } from 'react';
import { IEpInitialWizardSteps } from '../../interfaces';
import './EpWizard.scss';

interface EpWizardProps {
  wizardId: string;
  wizardSteps: IEpInitialWizardSteps[];
  allowDisorderlyNavigation?: boolean;
}

let currentActiveStep: number;
let setActiveStepFunc: React.Dispatch<React.SetStateAction<number>>;
let stepsWithStatus: IEpInitialWizardSteps[];
let setWizardStepsWithStatusFunc: React.Dispatch<
  React.SetStateAction<IEpInitialWizardSteps[]>
>;

const getUpdatedStepsWithStatus = (
  steps: IEpInitialWizardSteps[],
): IEpInitialWizardSteps[] => {
  const activeStepIndex: number =
    steps.findIndex((step) => step.isActive || step.isInitiallyActive) || 0;

  setActiveStepFunc(activeStepIndex);

  const formattedSteps: IEpInitialWizardSteps[] = steps.map(
    (step, index: number) => {
      const isActiveStep = index === activeStepIndex;
      const isDisabledStep: boolean =
        step.isDisabled || step.isInitiallyDisabled || false;
      const isConcludedStep: boolean = index < activeStepIndex && !isActiveStep;

      delete step.isInitiallyActive;
      delete step.isInitiallyDisabled;

      if (isActiveStep) {
        step.status = 'active';
        step.isActive = true;
        return step;
      }

      if (isDisabledStep) {
        step.status = 'disabled';
        step.isDisabled = true;
        return step;
      }

      if (isConcludedStep) {
        step.status = 'concluded';
        return step;
      }

      step.status = 'default';

      return step;
    },
  );

  return formattedSteps;
};

const updateActiveStep = (newActiveStepIndex: number): void => {
  const updatedStepsList: IEpInitialWizardSteps[] = stepsWithStatus?.map(
    (step: IEpInitialWizardSteps, index: number) => {
      step.isActive = index === newActiveStepIndex;
      return step;
    },
  );

  setWizardStepsWithStatusFunc(getUpdatedStepsWithStatus(updatedStepsList));
};

const findExpectedStepIndex = (type: 'previous' | 'next'): number => {
  if (type === 'next') {
    let possiblePreviousStepIndex = stepsWithStatus?.findIndex(
      (step) => step.status === 'default',
    );
    possiblePreviousStepIndex =
      possiblePreviousStepIndex <= 0
        ? currentActiveStep
        : possiblePreviousStepIndex;

    return possiblePreviousStepIndex;
  }

  const possiblePreviousStepIndexList: number[] = [];

  for (let i = 0; i < stepsWithStatus?.length; i++) {
    if (stepsWithStatus[i].status === 'concluded') {
      possiblePreviousStepIndexList.push(i);
    }
  }

  if (possiblePreviousStepIndexList.length <= 0) return currentActiveStep;

  return possiblePreviousStepIndexList[
    possiblePreviousStepIndexList.length - 1
  ];
};

export const skipToStep = (newStepIndex: number): void => {
  const currentActiveStepIndex: number =
    stepsWithStatus?.findIndex((step) => step.isActive) || 0;

  if (newStepIndex === currentActiveStepIndex) return;

  updateActiveStep(newStepIndex);
};

export const skipToStepReview = (newStepIndex: number): void => updateActiveStep(newStepIndex);

export const goToPrevStep = (): void => {
  const previousStepIndex = findExpectedStepIndex('previous');
  skipToStep(previousStepIndex);
};

export const goToNextStep = (): void => {
  const nextStepIndex = findExpectedStepIndex('next');
  skipToStep(nextStepIndex);
};

export const disableSteps = (steps: number[]): void => {
  const updatedStepsList: IEpInitialWizardSteps[] = stepsWithStatus?.map(
    (step: IEpInitialWizardSteps, index: number) => {
      step.isDisabled = steps.includes(index + 1);
      return step;
    },
  );

  setWizardStepsWithStatusFunc(getUpdatedStepsWithStatus(updatedStepsList));
};

const EpWizard: React.FC<EpWizardProps> = ({
  wizardId,
  wizardSteps,
  allowDisorderlyNavigation = false,
}) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [wizardStepsWithStatus, setWizardStepsWithStatus] = useState<
    IEpInitialWizardSteps[]
  >([]);

  const getStatusClass = (status: string): string => {
    const dictionary = {
      active: 'ep-wizard__step--active',
      concluded: 'ep-wizard__step--concluded',
      disabled: 'ep-wizard__step--disabled',
      default: '',
    };

    return dictionary[status as keyof typeof dictionary];
  };

  useEffect(() => {
    const handleActiveStep = (): void => {
      const activeStepInfos: IEpInitialWizardSteps | undefined =
        wizardStepsWithStatus?.find((step) => step.isActive);

      if (activeStepInfos && activeStepInfos.activeCallbackFunc) {
        activeStepInfos.activeCallbackFunc(activeStep);
      }
    };

    handleActiveStep();
  }, [activeStep, wizardStepsWithStatus]);

  useEffect(() => {
    stepsWithStatus = wizardStepsWithStatus;
  }, [wizardStepsWithStatus]);

  useEffect(() => {
    currentActiveStep = activeStep;
    setWizardStepsWithStatusFunc = setWizardStepsWithStatus;
    setActiveStepFunc = setActiveStep;
    setWizardStepsWithStatus(getUpdatedStepsWithStatus(wizardSteps));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div id={wizardId} className="ep-wizard">
      {wizardStepsWithStatus?.map(({ status, title }, index: number) => (
        <div
          key={`ep-wizard__step--${index}`}
          className={`ep-wizard__step ${getStatusClass(status || '')}`}
        >
          <button
            className="ep-wizard__step-number"
            style={{
              pointerEvents: `${
                allowDisorderlyNavigation && status !== 'disabled'
                  ? 'auto'
                  : 'none'
              }`,
            }}
            onClick={() => allowDisorderlyNavigation && skipToStep(index)}
          >
            {index + 1}
          </button>
          <h4 className="ep-wizard__step-title">{title}</h4>
        </div>
      ))}
    </div>
  );
};

export default EpWizard;
